import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { RootState } from "src/redux/reducers";
import {
  AUDIENCE_OVERLAP_POLLING_INTERVAL,
  AUDIENCE_OVERLAP_POLLING_MAX_ATTEMPTS,
  AUDIENCE_OVERLAP_POLLING_MAX_MISSING_STATES,
} from "./audienceOverlapUtils";
import { useShowcaseAudienceOverlap } from "./useShowcaseAudienceOverlap";

/**
 * Mounted once per showcase: waits for the pending analysis and reacts to changes of the set of creators.
 */
export function ShowcaseAudienceOverlapWatcher() {
  const audienceOverlap = useShowcaseAudienceOverlap();
  const { status } = audienceOverlap;

  // effects below must not restart on every render, so they reach the current handlers through a ref
  const latest = useRef(audienceOverlap);

  useEffect(() => {
    latest.current = audienceOverlap;
  });

  const elements = useSelector(
    (state: RootState) => state.showcaseReducer.showcaseData?.elements,
  );
  const idsKey = (elements || [])
    .map((element: { id: string }) => element.id)
    .join(",");
  const previousIdsKey = useRef(idsKey);

  useEffect(() => {
    if (status !== "pending") return undefined;

    let attempts = 0;
    let missingStates = 0;

    const interval = setInterval(async () => {
      attempts += 1;

      const polledStatus = await latest.current.poll();

      missingStates = polledStatus === "not_calculated" ? missingStates + 1 : 0;

      if (
        attempts >= AUDIENCE_OVERLAP_POLLING_MAX_ATTEMPTS ||
        missingStates >= AUDIENCE_OVERLAP_POLLING_MAX_MISSING_STATES
      ) {
        clearInterval(interval);
        latest.current.timeout();
      }
    }, AUDIENCE_OVERLAP_POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [status]);

  useEffect(() => {
    if (previousIdsKey.current === idsKey) return;

    const isInitialLoad = previousIdsKey.current === "";
    previousIdsKey.current = idsKey;

    if (isInitialLoad || latest.current.status === "off") return;

    // the showcase is being closed, there is nobody to inform
    if (idsKey === "") {
      latest.current.turnOff();

      return;
    }

    latest.current.restore();
  }, [idsKey]);

  useEffect(() => {
    return () => latest.current.turnOff();
  }, []);

  return null;
}
